import React, { useEffect, useState } from 'react';
import { Info } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';

// COMPONENTS
import RowLabel from '@/main/components/common/rowLabel/rowLabel';
import KogModalSideComponent from '@/main/components/modal/ModalSide';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import TooltipComponent from '@/main/components/shared/tooltip/tooltipComponent';

// REDUX
import { quoteApi } from '@/main/api';
import { ClaimDetailsResponse } from '@/main/models/response';
import { getTranslateByKey } from '@/main/services/utils';
import { formatCurrency } from '@/main/services/utilsNumber';

import * as S from './styles';
import SecondaryButton from '../button/secondaryButton';
import { useUserReducer } from '@/main/store/user';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';

interface IClaimDetailsProps {
  openModalClaimDetails: boolean;
  guidQuote: string;
  guidScreen?: string;
  showPrices?: boolean;
  setOpenModalClaimDetails: (value: boolean) => void;
}

const useStylesTooltip = makeStyles({
  popperCustom: { marginLeft: 15 }
});

const ClaimDetails: React.FC<IClaimDetailsProps> = ({
  openModalClaimDetails,
  guidQuote,
  guidScreen,
  showPrices = true,
  setOpenModalClaimDetails
}: IClaimDetailsProps) => {
  const classesTooltip = useStylesTooltip();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ClaimDetailsResponse | null>(null);

  useEffect(() => {
    if (openModalClaimDetails && guidQuote) {
      setIsLoading(true);
      quoteApi.getClaimDetails(String(guidQuote), guidScreen)
        .then(response => {
          if (response.data && response.data.result) {
            setData(response.data.result);
          } else {
            setData(null);
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [openModalClaimDetails, guidQuote]);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpenModalClaimDetails(true);
  };

  const { currentUser: userReducer } = useUserReducer();
  const userIsSupplier = userReducer?.idCompanyType === CompanyTypeEnum.Supplier;
  const canShowPrices = showPrices && !userIsSupplier;

  return (
    <>
      <TooltipComponent
        title={`${getTranslateByKey('information.clickToSeeClaimDetails')}`}
        placement="top"
        stylePopper={classesTooltip.popperCustom}>
        <IconButton
          size="medium"
          color="primary"
          style={{ marginLeft: 5 }}
          onClick={handleButtonClick}
          id="id-claimDetails-btn-seeClaimDetails">
          <Info />
        </IconButton>
      </TooltipComponent>
      <KogModalSideComponent
        open={openModalClaimDetails}
        title={getTranslateByKey('information.claimDetails')}
        closeModal={() => {
          setOpenModalClaimDetails(false);
        }}>
        <S.Container>
          {isLoading ? (
            <LoadingComponent />
          ) : data !== null ? (
            <S.ContainerInfo>
              <RowLabel
                label={getTranslateByKey('information.assessmentType')}
                content={data.assessmentTypeName}
              />
              <RowLabel
                label={getTranslateByKey('information.claimStatus')}
                content={data.claimStatus}
              />
              <RowLabel
                label={getTranslateByKey('information.Buyparts')}
                content={data.buyParts}
              />
              <RowLabel
                label={getTranslateByKey('information.claimType')}
                content={data.claimTypeName}
              />
              <RowLabel
                label={getTranslateByKey('information.policyNumber')}
                content={data.policyNumber}
              />
              <RowLabel
                label={getTranslateByKey('information.licensePlate')}
                content={data.vehicleRegistration}
              />
              <RowLabel
                label={getTranslateByKey('information.claimDate')}
                content={data.formattedClaimDate}
              />
              <RowLabel
                label={getTranslateByKey('information.authorizationDate')}
                content={data.formattedAuthorizationDate}
              />
              <RowLabel
                label={getTranslateByKey('information.approvedClaim')}
                content={data.claimApproved ?
                  getTranslateByKey('information.yes') :
                  getTranslateByKey('information.no')
                }
              />
              <RowLabel
                label={getTranslateByKey('information.authorizedBy')}
                content={data.authorizedBy}
              />
              {canShowPrices && (
                <RowLabel
                  label={getTranslateByKey('information.deductible')}
                  content={data.deductible ?
                    formatCurrency(data.deductible) :
                    ''}
                />
              )}
              <RowLabel
                label={getTranslateByKey('information.deductibleType')}
                content={data.deductibleType}
              />
              <RowLabel
                label={getTranslateByKey('information.claimNumberSiniestro')}
                content={data.adminData}
              />
              {canShowPrices && (
                <>
                  <RowLabel
                    label={getTranslateByKey('information.vehicleValue')}
                    content={data.vehicleValue ?
                      formatCurrency(data.vehicleValue) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.paintingValuePerHour')}
                    content={data.paintingValuePerHour ?
                      formatCurrency(data.paintingValuePerHour) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.paintingMaterialAmount')}
                    content={data.paintingMaterialAmount ?
                      formatCurrency(data.paintingMaterialAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.paintingLabourAmount')}
                    content={data.paintingLabourAmount ?
                      formatCurrency(data.paintingLabourAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.repairValuePerHour')}
                    content={data.repairValuePerHour ?
                      formatCurrency(data.repairValuePerHour) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.repairLabourAmount')}
                    content={data.repairLabourAmount ?
                      formatCurrency(data.repairLabourAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.totalPartsAmount')}
                    content={data.totalPartsAmount ?
                      formatCurrency(data.totalPartsAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.totalOtherAmount')}
                    content={data.totalOtherAmount ?
                      formatCurrency(data.totalOtherAmount) :
                      ''}
                  />
                  <RowLabel
                    label={getTranslateByKey('information.repairAndPaintTotalAmountWithVAT')}
                    content={data.totalRepairWithoutVAT ?
                      formatCurrency(data.totalRepairWithoutVAT) :
                      ''}
                  />
                </>
              )}
            </S.ContainerInfo>
          ) : (
            <ResultNotFoundComponent />
          )}
          <S.AreaButtons>
            <SecondaryButton
              id="id-claimDetails-btn-back"
              onClick={() => setOpenModalClaimDetails(false)}
            >
              {getTranslateByKey('information.back')}
            </SecondaryButton>
          </S.AreaButtons>
        </S.Container>
      </KogModalSideComponent>
    </>
  );
};

export default ClaimDetails;
